import { differenceInYears, format, parse } from 'date-fns'

import { LTsessionTypes } from './constants'
import {
  ClientObject,
  EAPAndHPIProgramSupport,
  LTProgramName,
  MEETING_FORMATS,
  ProgramCoverageBreakdown,
  ProgramNames,
  YearProgramCoverage,
} from '../../models'
import { FeedbackResponse } from '../../models/feedback/Feedback'
import { ProgramCustomerPropertyId } from '../common/constants/customerProgram'
import { FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS } from '../common/constants/healthPlanAndPaymentInformationConstants'
import { assertUnreachable } from '../common/utils/commonUtils'
import { convertDateStringFromISOToUSFormat } from '../common/utils/dateUtils'
import { isMinor } from '../searchForCare/utils'

export const translateFeedbackResponse = (response: { [key: string]: any }): FeedbackResponse[] =>
  Object.keys(response).map((key) => ({ question: key, answer: response[key].toString() }))

export const programSupportForAttribute = (
  attribute: keyof EAPAndHPIProgramSupport,
  programConfig?: { programCoverageName?: ProgramCustomerPropertyId },
  selectedEpisode?: {
    program_name?: string
  },
  programCoverageBreakdown?: ProgramCoverageBreakdown,
) => {
  const currentProgramCoverageBreakdown =
    programCoverageBreakdown && programCoverageBreakdown[format(new Date(), 'yyyy')]

  const programCoverageName = programConfig?.programCoverageName
  const validProgramCoverage =
    programCoverageName &&
    selectedEpisode?.program_name &&
    currentProgramCoverageBreakdown &&
    programCoverageName in currentProgramCoverageBreakdown

  return (
    attribute &&
    programConfig &&
    validProgramCoverage &&
    currentProgramCoverageBreakdown[programCoverageName as keyof YearProgramCoverage]?.[attribute]
  )
}

/**
 * Determines if EAP session limit information should be shown for a client
 *
 * EAP session limit info should not be shown for adults when forceAdultFlowsToHPI is true, the client's company is Amazon, and the program is not coaching for parents
 *
 * @param forceAdultFlowsToHPI - LD flag
 * @param clientEmployer - Client's employer
 * @param clientDateOfBirth - Client's date of birth
 * @param programName - Program name
 * @returns True if EAP session limit information should be shown, false otherwise
 */
export const shouldShowEAPSessionLimitInfo = (
  forceAdultFlowsToHPI: boolean,
  clientEmployer?: string,
  clientDateOfBirth?: string,
  programName?: string,
): boolean => {
  const isClientMinor = clientDateOfBirth ? isMinor(convertDateStringFromISOToUSFormat(clientDateOfBirth) ?? '') : false

  return !(
    forceAdultFlowsToHPI &&
    FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS.includes(clientEmployer ?? '') &&
    !isClientMinor &&
    programName !== ProgramNames.CoachingForParents
  )
}

/**
 * Resolves the appropriate session limit based on the program name and client details
 *
 * For CoachingForParents program, returns cfp_sessions_limit if the client's employer is in FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS and forceAdultFlowsToHPI is true
 * For all other programs, returns sessions_limit
 *
 * The CFP session limit is only used for Amazon.
 *
 * @param programName - Program name
 * @param clientDetails - Client details containing session limits
 * @param forceAdultFlowsToHPI - LD flag
 * @returns Session limit or CFP session limit if the customer is Amazon
 */
export const resolveSessionLimit = (
  programName?: string,
  clientDetails?: {
    cfp_sessions_limit?: number
    sessions_limit?: number
    eap_session_limit?: number
    employer?: string
  } | null,
  forceAdultFlowsToHPI: boolean = false,
  isEAP: boolean = false,
): number | undefined => {
  if (!clientDetails) {
    return undefined
  }

  if (
    forceAdultFlowsToHPI &&
    FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS.includes(clientDetails.employer ?? '') &&
    programName === ProgramNames.CoachingForParents
  ) {
    return clientDetails.cfp_sessions_limit
  }

  return isEAP ? clientDetails.eap_session_limit : clientDetails.sessions_limit
}

/**
 * For adult clients of some specific customers (Amazon), return a new program coverage object with EAP coverage removed for the following programs:
 *
 * considerLyraTherapy, directAccessTherapy
 *
 * These are programs that should have EAP enabled only for minor clients.
 *
 * For any other customer or minors, return the same customer program coverage object.
 *
 */
export function getModifiedProgramCoverageForBenefitsEligibility(
  forceAdultFlowsToHPI: boolean,
  currentProgramCoverageBreakdown: YearProgramCoverage,
  clientDetails?: Pick<ClientObject, 'date_of_birth' | 'employer'>,
): YearProgramCoverage {
  if (
    forceAdultFlowsToHPI &&
    clientDetails?.employer &&
    FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS.includes(clientDetails.employer) &&
    clientDetails?.date_of_birth &&
    !isMinor(convertDateStringFromISOToUSFormat<never>(clientDetails.date_of_birth)) &&
    (currentProgramCoverageBreakdown.considerLyraTherapy || currentProgramCoverageBreakdown.directAccessTherapy)
  ) {
    return {
      ...currentProgramCoverageBreakdown,
      ...(currentProgramCoverageBreakdown.considerLyraTherapy
        ? {
            considerLyraTherapy: {
              ...currentProgramCoverageBreakdown.considerLyraTherapy,
              eapSupported: false,
            },
          }
        : undefined),
      ...(currentProgramCoverageBreakdown.directAccessTherapy
        ? {
            directAccessTherapy: {
              ...currentProgramCoverageBreakdown.directAccessTherapy,
              eapSupported: false,
            },
          }
        : undefined),
    }
  }

  return currentProgramCoverageBreakdown
}

const INITIAL_DA_SESSION_TYPES = [
  LTsessionTypes.INITIAL_DA_SESSION,
  LTsessionTypes.INITIAL_DA_COUPLES_SESSION,
  LTsessionTypes.INITIAL_DA_FAMILY_SESSION,
  LTsessionTypes.INITIAL_DA_CHILD_SESSION,
]

const getValidBaseRate = (
  sessionType: string,
  sessionsRates: { [key: string]: string },
): { rate: string; parsedRate: number } => {
  const baseRate = sessionsRates[sessionType]
  const parsedBaseRate = parseFloat(baseRate)

  if (baseRate != null && !isNaN(parsedBaseRate) && parsedBaseRate > 0) {
    return { rate: baseRate, parsedRate: parsedBaseRate }
  }

  const isInitialSession = INITIAL_DA_SESSION_TYPES.includes(sessionType)
  const fallbackSessionType = isInitialSession ? LTsessionTypes.INITIAL_DA_SESSION : LTsessionTypes.FOLLOWUP_DA_SESSION
  const fallbackRate = sessionsRates[fallbackSessionType]
  const parsedFallbackRate = parseFloat(fallbackRate)

  if (fallbackRate != null && !isNaN(parsedFallbackRate) && parsedFallbackRate > 0) {
    return { rate: fallbackRate, parsedRate: parsedFallbackRate }
  }

  return { rate: '0', parsedRate: 0 }
}

export const calculateSessionRate = (
  sessionType: string,
  sessionsRates: { [key: string]: string },
  modality?: string,
  differentialPricingCalculationEnabled?: boolean,
  differentialPricingEnabled?: boolean,
  differentialPricingModifier?: number,
): string => {
  // NOTE: EndOfSession passes two different values for phone modality: phone and tele_therapy.
  // If we need to add logic that uses this modality we would have to use both these values - similar to how we handle in_person
  const { rate: baseRate, parsedRate: parsedBaseRate } = getValidBaseRate(sessionType, sessionsRates)
  const hasValidBaseRate = parsedBaseRate > 0

  if (differentialPricingCalculationEnabled) {
    if (
      differentialPricingEnabled &&
      (modality === MEETING_FORMATS.IN_PERSON || modality === MEETING_FORMATS.IN_PERSON_UNDERSCORE) &&
      differentialPricingModifier &&
      hasValidBaseRate
    ) {
      return (differentialPricingModifier + parsedBaseRate).toString()
    } else {
      return baseRate
    }
  } else {
    return baseRate
  }
}

export const isDateOfBirthUnder18 = (dateOfBirth: string) => {
  if (!dateOfBirth) {
    return false
  }

  try {
    // Determine the separator and format based on the input string
    const hasDash = dateOfBirth.includes('-')
    const hasSlash = dateOfBirth.includes('/')

    let birthDate: Date

    if (hasDash && !hasSlash) {
      // Parse the date string in YYYY-MM-DD format
      birthDate = parse(dateOfBirth, 'yyyy-MM-dd', new Date())
    } else if (hasSlash && !hasDash) {
      // Parse the date string in YYYY/MM/DD format
      birthDate = parse(dateOfBirth, 'yyyy/MM/dd', new Date())
    } else {
      // Invalid format or mixed separators
      return false
    }

    // Check if the parsed date is valid
    if (isNaN(birthDate.getTime())) {
      return false
    }

    const today = new Date()
    const age = differenceInYears(today, birthDate)

    return age < 18
  } catch {
    return false
  }
}

export const getLTSessionTypeAndRate = ({
  programName,
  sessionNumber,
  sessionsRates = {},
  modality,
  differentialPricingCalculationEnabled,
  differentialPricingEnabled,
  differentialPricingModifier,
  dateOfBirth,
  isInternationalCoachingSessionTypeEnabled,
  clientCountry,
}: {
  programName: LTProgramName
  sessionNumber: number
  sessionsRates: { [key: string]: string }
  modality?: string
  differentialPricingCalculationEnabled?: boolean
  differentialPricingEnabled?: boolean
  differentialPricingModifier?: number
  dateOfBirth?: string
  isInternationalCoachingSessionTypeEnabled?: boolean
  clientCountry?: string
}): { sessionType: string; sessionRate: string } => {
  let sessionType, sessionRate
  switch (programName) {
    case ProgramNames.BlendedCareTherapy:
      sessionType = LTsessionTypes.BCT_SESSION
      sessionRate = '0'
      break
    case ProgramNames.DialecticalBehaviorTherapy:
      sessionType = LTsessionTypes.DBT_SESSION
      sessionRate = '0'
      break
    case ProgramNames.MedicationManagement:
      sessionType = sessionNumber > 1 ? LTsessionTypes.FOLLOWUP_MEDS_SESSION : LTsessionTypes.INITIAL_MEDS_SESSION
      sessionRate = '0'
      break
    case ProgramNames.Coaching:
      if (isInternationalCoachingSessionTypeEnabled && clientCountry !== 'US') {
        sessionType = LTsessionTypes.INTL_SMP_SESSION
      } else {
        sessionType = LTsessionTypes.SMP_SESSION
      }
      sessionRate = sessionsRates[sessionType] || '0'
      break
    case ProgramNames.SingleSessionCoaching:
      sessionType = LTsessionTypes.SSC_SESSION
      sessionRate = sessionsRates[sessionType] || '0'
      break
    case ProgramNames.CoachingForParents:
      sessionType = LTsessionTypes.COACHING_FOR_PARENTS_SESSION
      sessionRate = sessionsRates[sessionType] || '0'
      break
    case ProgramNames.AlcoholUseDisorderTherapy:
      sessionType = LTsessionTypes.AUD_SESSION
      sessionRate = '0'
      break
    case ProgramNames.ClinicalLeaveEvaluation:
      sessionType = sessionNumber > 1 ? LTsessionTypes.FOLLOWUP_CLE_SESSION : LTsessionTypes.INITIAL_CLE_SESSION
      sessionRate = '0'
      break
    case ProgramNames.TeensTherapy:
      sessionType = LTsessionTypes.LCTT_SESSION
      sessionRate = '0'
      break
    case ProgramNames.DirectAccessCouplesTherapy:
      sessionType =
        sessionNumber > 1 ? LTsessionTypes.FOLLOWUP_DA_COUPLES_SESSION : LTsessionTypes.INITIAL_DA_COUPLES_SESSION
      sessionRate = calculateSessionRate(
        sessionType,
        sessionsRates,
        modality,
        differentialPricingCalculationEnabled,
        differentialPricingEnabled,
        differentialPricingModifier,
      )
      break
    case ProgramNames.DirectAccessIndividualChildTherapy:
      sessionType =
        sessionNumber > 1 ? LTsessionTypes.FOLLOWUP_DA_CHILD_SESSION : LTsessionTypes.INITIAL_DA_CHILD_SESSION
      sessionRate = calculateSessionRate(
        sessionType,
        sessionsRates,
        modality,
        differentialPricingCalculationEnabled,
        differentialPricingEnabled,
        differentialPricingModifier,
      )
      break
    case ProgramNames.DirectAccessIndividualAdolescentTherapy:
      sessionType =
        sessionNumber > 1 ? LTsessionTypes.FOLLOWUP_DA_CHILD_SESSION : LTsessionTypes.INITIAL_DA_CHILD_SESSION
      sessionRate = calculateSessionRate(
        sessionType,
        sessionsRates,
        modality,
        differentialPricingCalculationEnabled,
        differentialPricingEnabled,
        differentialPricingModifier,
      )
      break
    case ProgramNames.DirectAccessIndividualTherapy:
      sessionType =
        dateOfBirth && isDateOfBirthUnder18(dateOfBirth)
          ? sessionNumber > 1
            ? LTsessionTypes.FOLLOWUP_DA_CHILD_SESSION
            : LTsessionTypes.INITIAL_DA_CHILD_SESSION
          : sessionNumber > 1
          ? LTsessionTypes.FOLLOWUP_DA_SESSION
          : LTsessionTypes.INITIAL_DA_SESSION
      sessionRate = calculateSessionRate(
        sessionType,
        sessionsRates,
        modality,
        differentialPricingCalculationEnabled,
        differentialPricingEnabled,
        differentialPricingModifier,
      )
      break
    case ProgramNames.DirectAccessFamilyTherapy:
      sessionType =
        sessionNumber > 1 ? LTsessionTypes.FOLLOWUP_DA_FAMILY_SESSION : LTsessionTypes.INITIAL_DA_FAMILY_SESSION
      sessionRate = calculateSessionRate(
        sessionType,
        sessionsRates,
        modality,
        differentialPricingCalculationEnabled,
        differentialPricingEnabled,
        differentialPricingModifier,
      )
      break
    default:
      // Type assertion on programName forces switch statement to include all ProgramNames
      assertUnreachable(programName, 'Unknown program name in getLTSessionTypeAndRate: ' + programName)
  }
  return { sessionType, sessionRate } as { sessionType: string; sessionRate: string }
}
